"use client";

import { PlugIcon } from "lucide-react";

import type {
  McpApprovalRequestContentBlock,
  McpApprovalResponseContentBlock,
  McpCallContentBlock,
  McpListToolsContentBlock,
} from "../../types";
import { cn } from "../../utils";
import { CollapsibleSection } from "../CollapsibleSection";

// ---------------------------------------------------------------------------
// McpListToolsBlock
// ---------------------------------------------------------------------------

interface McpListToolsBlockProps {
  block: McpListToolsContentBlock;
}

function formatMcpError(
  error: string | Record<string, unknown> | null | undefined,
): string {
  if (!error) {
    return "";
  }
  if (typeof error === "string") {
    return error;
  }
  if (typeof error === "object" && error !== null) {
    if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
    try {
      return JSON.stringify(error, null, 2);
    } catch {
      return String(error);
    }
  }
  return String(error);
}

export const McpListToolsBlock = ({ block }: McpListToolsBlockProps) => {
  const toolCount = block.tools.length;
  const label = `${block.server_label} (${toolCount} ${toolCount === 1 ? "tool" : "tools"})`;
  const hasError = !!block.error;

  const copyContent = block.tools.map((t) => t.name).join(", ");

  return (
    <CollapsibleSection
      icon={
        <PlugIcon
          className={cn("h-3 w-3", hasError ? "text-red-500" : "text-blue-500")}
        />
      }
      label={label}
      preview={
        hasError
          ? formatMcpError(block.error)
          : block.tools.map((t) => t.name).join(", ")
      }
      copyContent={copyContent}
      variant={hasError ? "error" : "default"}
    >
      <div className="flex flex-col gap-1">
        {block.tools.map((tool, i) => (
          <div
            // eslint-disable-next-line react/no-array-index-key
            key={`${tool.name}-${i}`}
            className="rounded-md border border-zinc-200 bg-zinc-50/50 px-2 py-1.5"
          >
            <div className="text-[11px] font-medium text-zinc-700">
              {tool.name}
            </div>
            {tool.description && (
              <div className="mt-0.5 text-[10px] text-zinc-500">
                {tool.description}
              </div>
            )}
          </div>
        ))}
        {hasError && (
          <div className="mt-1 text-xs text-red-500">
            {formatMcpError(block.error)}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// McpCallBlock
// ---------------------------------------------------------------------------

interface McpCallBlockProps {
  block: McpCallContentBlock;
  /** Map of approval_request_id → approve boolean, used to show resolution state */
  approvalMap?: Map<string, boolean>;
}

function prettyPrint(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export const McpCallBlock = ({ block, approvalMap }: McpCallBlockProps) => {
  const hasError = !!block.error;
  const wasApproved =
    block.approval_request_id && approvalMap
      ? approvalMap.get(block.approval_request_id)
      : undefined;

  let variant: "default" | "error" = "default";
  let iconColor = "text-zinc-500";
  let statusBadge: string | null = null;

  if (hasError) {
    variant = "error";
    iconColor = "text-red-500";
    statusBadge = "failed";
  } else if (wasApproved === true) {
    iconColor = "text-green-600";
    statusBadge = "approved";
  } else if (wasApproved === false) {
    iconColor = "text-red-500";
    statusBadge = "denied";
  }

  const formattedArgs = prettyPrint(block.arguments);

  const headerExtra = statusBadge ? (
    <span
      className={cn(
        "inline-flex rounded-full px-1.5 py-0.5 text-[9px] font-medium",
        statusBadge === "failed" && "bg-red-50 text-red-600",
        statusBadge === "approved" && "bg-green-50 text-green-600",
        statusBadge === "denied" && "bg-red-50 text-red-500",
      )}
    >
      {statusBadge}
    </span>
  ) : null;

  // eslint-disable-next-line no-nested-ternary
  const preview = block.output
    ? block.output.length > 80
      ? block.output.slice(0, 80) + "..."
      : block.output
    : formattedArgs.length > 80
      ? formattedArgs.slice(0, 80) + "..."
      : formattedArgs;

  return (
    <CollapsibleSection
      icon={<PlugIcon className={cn("h-3 w-3", iconColor)} />}
      label={block.name}
      headerExtra={headerExtra}
      preview={preview}
      copyContent={block.output || formattedArgs}
      variant={variant}
    >
      <div className="flex flex-col gap-1.5">
        {block.server_label && (
          <div className="text-[10px] text-zinc-400">
            <span className="font-medium">Server:</span> {block.server_label}
          </div>
        )}
        <div className="text-[10px] text-zinc-400">Arguments:</div>
        <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 text-xs text-zinc-800">
          {formattedArgs}
        </pre>
        {block.output && (
          <>
            <div className="text-[10px] text-zinc-400">Output:</div>
            <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 text-xs text-zinc-800">
              {block.output}
            </pre>
          </>
        )}
        {hasError && (
          <div className="mt-1 text-xs text-red-500">
            {formatMcpError(block.error)}
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// McpApprovalRequestBlock
// ---------------------------------------------------------------------------

interface McpApprovalRequestBlockProps {
  block: McpApprovalRequestContentBlock;
  /** Whether this approval request has already been resolved */
  isResolved?: boolean;
}

export const McpApprovalRequestBlock = ({
  block,
  isResolved = false,
}: McpApprovalRequestBlockProps) => {
  const formattedArgs = prettyPrint(block.arguments);

  return (
    <CollapsibleSection
      icon={
        <PlugIcon
          className={cn(
            "h-3 w-3",
            isResolved ? "text-zinc-400" : "text-amber-500",
          )}
        />
      }
      label={block.name}
      headerExtra={
        !isResolved ? (
          <div className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />
            <span className="text-[10px] text-amber-500">
              Awaiting approval
            </span>
          </div>
        ) : (
          <span className="text-[10px] text-zinc-400">Resolved</span>
        )
      }
      preview={
        formattedArgs.length > 80
          ? formattedArgs.slice(0, 80) + "..."
          : formattedArgs
      }
      copyContent={formattedArgs}
      variant={isResolved ? "default" : "tool"}
    >
      <div className="flex flex-col gap-1.5">
        {block.server_label && (
          <div className="text-[10px] text-zinc-400">
            <span className="font-medium">Server:</span> {block.server_label}
          </div>
        )}
        <div className="text-[10px] text-zinc-400">Arguments:</div>
        <pre className="overflow-x-auto rounded-md bg-zinc-100 p-2 text-xs text-zinc-800">
          {formattedArgs}
        </pre>
      </div>
    </CollapsibleSection>
  );
};

// ---------------------------------------------------------------------------
// McpApprovalResponseBlock
// ---------------------------------------------------------------------------

interface McpApprovalResponseBlockProps {
  block: McpApprovalResponseContentBlock;
}

export const McpApprovalResponseBlock = ({
  block,
}: McpApprovalResponseBlockProps) => {
  const approved = block.approve;

  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md border px-2.5 py-1.5",
        approved
          ? "border-green-200 bg-green-50 text-green-700"
          : "border-red-200 bg-red-50 text-red-700",
      )}
    >
      <PlugIcon
        className={cn("h-3 w-3", approved ? "text-green-500" : "text-red-500")}
      />
      <span className="text-sm font-medium">
        {approved ? "Approved" : "Denied"}
      </span>
    </div>
  );
};
